// Loop-aware block editing for the workflow builder.
//
// A loop repeats the blocks from the one directly below it through its
// `bodyEnd` (schema.loopSpan). These edits keep that pointer on the block the
// owner sees as the loop's last one when blocks are inserted, moved or
// removed, and never let one loop end up inside another. Pure: the builder
// applies the returned definition.

import {
  loopSpans,
  type WorkflowDefinition,
  type WorkflowNode,
} from "./schema.ts";

type Def = WorkflowDefinition;

function withBodyEnd(
  nodes: WorkflowNode[],
  loopId: string,
  bodyEnd: string | undefined,
): WorkflowNode[] {
  return nodes.map((n) => {
    if (n.id !== loopId) return n;
    const parameters = { ...n.parameters };
    if (bodyEnd === undefined) delete parameters.bodyEnd;
    else parameters.bodyEnd = bodyEnd;
    return { ...n, parameters };
  });
}

/** Move nodes[start..end] so they sit in front of the node that was at
 *  `before` (an index into the ORIGINAL array; may be nodes.length). */
function relocate(
  nodes: WorkflowNode[],
  start: number,
  end: number,
  before: number,
): WorkflowNode[] {
  const group = nodes.slice(start, end + 1);
  const rest = [...nodes.slice(0, start), ...nodes.slice(end + 1)];
  const at = before > end ? before - group.length : before;
  return [...rest.slice(0, at), ...group, ...rest.slice(at)];
}

/**
 * Insert `node` at `index`. A new loop arrives with `body` (its first blocks)
 * directly below it. `intoLoopEnd` is the id of a loop whose last block sits
 * just above `index`: the new block joins that loop as its new last block.
 */
export function insertBlock(
  def: Def,
  index: number,
  node: WorkflowNode,
  opts: { body?: WorkflowNode[]; intoLoopEnd?: string } = {},
): Def {
  const added = [node, ...(node.type === "loop" ? opts.body ?? [] : [])];
  let nodes = [
    ...def.nodes.slice(0, index),
    ...added,
    ...def.nodes.slice(index),
  ];
  if (node.type === "loop" && added.length > 1) {
    nodes = withBodyEnd(nodes, node.id, added[added.length - 1].id);
  }
  if (opts.intoLoopEnd && node.type !== "loop") {
    nodes = withBodyEnd(nodes, opts.intoLoopEnd, node.id);
  }
  return { ...def, nodes };
}

/** Remove a block. Removing a loop keeps its blocks (they just stop
 *  repeating); removing a loop's last block makes the one above it last. */
export function removeBlock(def: Def, id: string): Def {
  const index = def.nodes.findIndex((n) => n.id === id);
  if (index < 0) return def;
  let nodes = def.nodes;
  for (const span of loopSpans(def)) {
    if (span.endIndex !== index) continue;
    const previous = index - 1 >= span.startIndex
      ? def.nodes[index - 1].id
      : undefined;
    nodes = withBodyEnd(nodes, span.loopId, previous);
  }
  return { ...def, nodes: nodes.filter((n) => n.id !== id) };
}

/**
 * Move a block one step up (-1) or down (+1).
 *  - A loop moves together with all its blocks, and hops over a whole
 *    neighbouring loop rather than landing inside it.
 *  - A block inside a loop leaves it past the top or the bottom edge.
 *  - A block next to a loop joins it: from below as its last block, from
 *    above as its first.
 * The trigger never moves and nothing moves above it.
 */
export function moveBlock(def: Def, id: string, dir: -1 | 1): Def {
  const nodes = def.nodes;
  const i = nodes.findIndex((n) => n.id === id);
  if (i <= 0) return def;
  const spans = loopSpans(def);
  const inside = spans.find((sp) => i >= sp.startIndex && i <= sp.endIndex);

  if (inside) {
    if (dir === -1 && i === inside.startIndex) {
      // Out past the top: it goes above the loop.
      const next = relocate(nodes, i, i, inside.loopIndex);
      return {
        ...def,
        nodes: i === inside.endIndex
          ? withBodyEnd(next, inside.loopId, undefined)
          : next,
      };
    }
    if (dir === 1 && i === inside.endIndex) {
      // Out past the bottom: it stays put and the loop ends above it.
      const previous = i - 1 >= inside.startIndex ? nodes[i - 1].id : undefined;
      return { ...def, nodes: withBodyEnd(nodes, inside.loopId, previous) };
    }
    // Reorder within the loop. The last position stays the loop's end.
    const next = relocate(nodes, i, i, dir === -1 ? i - 1 : i + 2);
    return {
      ...def,
      nodes: withBodyEnd(next, inside.loopId, next[inside.endIndex].id),
    };
  }

  const own = spans.find((sp) => sp.loopIndex === i);
  const groupEnd = own ? own.endIndex : i;
  if (dir === -1) {
    const above = i - 1;
    if (above <= 0) return def; // the trigger stays first
    const aboveLoop = spans.find((sp) => sp.endIndex === above);
    if (aboveLoop && own) {
      return { ...def, nodes: relocate(nodes, i, groupEnd, aboveLoop.loopIndex) };
    }
    if (aboveLoop) {
      // Joins the loop above as its last block; nothing moves.
      return { ...def, nodes: withBodyEnd(nodes, aboveLoop.loopId, nodes[i].id) };
    }
    return { ...def, nodes: relocate(nodes, i, groupEnd, above) };
  }
  const below = groupEnd + 1;
  if (below >= nodes.length) return def;
  const belowLoop = spans.find((sp) => sp.loopIndex === below);
  if (belowLoop && own) {
    return { ...def, nodes: relocate(nodes, i, groupEnd, belowLoop.endIndex + 1) };
  }
  // A plain block swapping with a loop block becomes that loop's first block.
  return { ...def, nodes: relocate(nodes, i, groupEnd, below + 1) };
}

/** True when the block below a loop can be pulled into it. */
export function canIncludeNextBlock(def: Def, loopId: string): boolean {
  const loopIndex = def.nodes.findIndex((n) => n.id === loopId);
  if (loopIndex < 0) return false;
  const span = loopSpans(def).find((sp) => sp.loopId === loopId);
  const next = def.nodes[(span ? span.endIndex : loopIndex) + 1];
  return !!next && next.type !== "loop" && next.type !== "trigger";
}

/** Pull the block just below a loop into it, as its new last block. */
export function includeNextBlock(def: Def, loopId: string): Def {
  if (!canIncludeNextBlock(def, loopId)) return def;
  const loopIndex = def.nodes.findIndex((n) => n.id === loopId);
  const span = loopSpans(def).find((sp) => sp.loopId === loopId);
  const next = def.nodes[(span ? span.endIndex : loopIndex) + 1];
  return { ...def, nodes: withBodyEnd(def.nodes, loopId, next.id) };
}

/** Let a loop's last block go: it stays where it is, now after the loop. */
export function releaseLastBlock(def: Def, loopId: string): Def {
  const span = loopSpans(def).find((sp) => sp.loopId === loopId);
  if (!span) return def;
  const previous = span.endIndex - 1 >= span.startIndex
    ? def.nodes[span.endIndex - 1].id
    : undefined;
  return { ...def, nodes: withBodyEnd(def.nodes, loopId, previous) };
}
